'use client'

import { Box, SimpleGrid, Skeleton, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Suspense } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import InfoTooltip from '@/app/[locale]/price/stocks/[symbol]/_components/MarketData/InfoTooltip'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { useStockBySymbol } from '@/services/stock/meta'
import { formatMarketCap } from '@/utils/number/format'

import DefinitionsBanner from './DefinitionsBanner'

function MarketDataFallback() {
  return (
    <Box mt={{ base: 40, medium: 80 }} style={{ borderRadius: 12, width: '100%' }}>
      <Skeleton
        h={32}
        w={160}
        radius={40}
        mb={12}
        style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
      />
      <SimpleGrid cols={{ base: 2, medium: 4 }} spacing={16}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map(index => (
          <Box key={index}>
            <Skeleton
              h={16}
              w={120}
              mb={8}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />
            <Skeleton
              h={20}
              w={80}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  )
}

function MarketDataContent() {
  const t = useTranslations('marketDataDefinitions')
  const { coin } = useCoinContext()
  const { data: asset } = useStockBySymbol(coin.symbol)
  const { selectedCurrency } = useSsrContext()
  const currentCurrency = getCurrencyInfo(selectedCurrency) || DEFAULT_CURRENCY
  const sym = currentCurrency.symbol

  const contentItems = [
    {
      title: t('marketCap'),
      value: asset?.market_cap ? `${sym}${formatMarketCap(parseFloat(asset.market_cap))}` : '',
    },
    {
      title: t('volume'),
      value: asset?.volume ? `${formatMarketCap(parseFloat(asset.volume))}` : '',
    },
    {
      title: t('pe'),
      value: asset?.pe_ratio ? parseFloat(asset.pe_ratio).toFixed(2) : '',
    },
    {
      title: t('eps'),
      value: asset?.eps
        ? (() => {
            const v = parseFloat(asset.eps)
            const sign = v < 0 ? '-' : ''
            return `${sign}${sym}${Math.abs(v).toFixed(2)}`
          })()
        : '',
    },
    {
      title: t('week52High'),
      value: asset?.week52_high_price
        ? `${sym}${parseFloat(asset.week52_high_price).toFixed(2)}`
        : '',
    },
    {
      title: t('week52Low'),
      value: asset?.week52_low_price
        ? `${sym}${parseFloat(asset.week52_low_price).toFixed(2)}`
        : '',
    },
    {
      title: t('dividendYield'),
      value: asset?.dividend_yield ? `${asset.dividend_yield}%` : '',
    },
    {
      title: t('dividendPerShare'),
      value: asset?.dividend_price ? `${sym}${parseFloat(asset.dividend_price).toFixed(2)}` : '',
    },
  ]

  if (!asset) return null
  const filteredContentItems = contentItems.filter(item => item.value !== '')

  return (
    <Box mt={{ base: 40, medium: 80 }} style={{ borderRadius: 12, width: '100%' }}>
      <Text component="h2" variant="heading2" mb={4}>
        {t('sectionTitle')}
      </Text>
      <Text
        mb={12}
        style={{
          fontSize: 17,
          fontWeight: 500,
          lineHeight: '20px',
          color: 'var(--content-secondary)',
        }}
      >
        {t('sectionSubtitle')}
      </Text>
      <SimpleGrid cols={{ base: 2, medium: 4 }} spacing={16} mb={8}>
        {filteredContentItems.map((item, index) => (
          <InfoTooltip key={index} title={item.title} content={item.value} />
        ))}
      </SimpleGrid>
      <DefinitionsBanner />
    </Box>
  )
}

function MarketData() {
  return (
    <Suspense fallback={<MarketDataFallback />}>
      <MarketDataContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(MarketData)
